/**
 * The procedural pixel-art tone atlas of the Medium tier's surfaces (lifted
 * from the dungeon-floors prototype's `dungeon-pixel-atlas.js`). One 256×256
 * RGBA8 texture per distinct set of generator inputs, cached for the page.
 * Channels hold tones (0..1), not colours: R ground dirt and gravel, G
 * sedimentary strata, B timber planks, A rubble rock caps. The surface shader
 * quantizes a tone to a theme ramp with an ordered dither, so a theme's
 * colours never need a new texture. Nearest filtering, no mipmaps, repeat
 * wrap. The textures are borrowed by every floor: floor disposal never
 * disposes them. `freeAtlasGpu` frees their GPU copies (a view going to Off;
 * the next use uploads them again); each Medium view holds the cache through
 * `holdAtlases`, and when the last holder lets go the textures are disposed
 * and the cache emptied.
 */
import type { Theme } from '@ab/content-schema';
import * as THREE from 'three';
import { presentation } from './constants.ts';

const k = presentation.medium.atlas;
const SIZE = k.size;
const cache = new Map<string, THREE.DataTexture>();

type Options = { grain: number; pebble: number; crack: number; wobble: number; bandMin: number; bandMax: number };
type Random = () => number;
type Sampler = (x: number, y: number) => number;

/** Tone scale: seven nominal levels; ramps of any length map linearly. */
const L = (n: number): number => n / 6;
/** mulberry32, the prototype's seeded generator. */
const rng = (seed: number): Random => {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
const smooth = (t: number): number => t * t * (3 - 2 * t);
const lattice = (r: Random, cx: number, cy: number): Float32Array => {
  const g = new Float32Array(cx * cy);
  for (let i = 0; i < g.length; i += 1) g[i] = r();
  return g;
};
const at = (x: number, y: number): number => (((y % SIZE) + SIZE) % SIZE) * SIZE + (((x % SIZE) + SIZE) % SIZE);
const get = (a: Float32Array, i: number): number => a[i] ?? 0;

/** Tileable value noise over the atlas period, `cx × cy` lattice cells. */
const noise = (r: Random, cx: number, cy = cx): Sampler => {
  const g = lattice(r, cx, cy);
  return (x, y) => {
    const fx = x / (SIZE / cx);
    const fy = y / (SIZE / cy);
    const ix = Math.floor(fx);
    const iy = Math.floor(fy);
    const u = smooth(fx - ix);
    const v = smooth(fy - iy);
    const x0 = ((ix % cx) + cx) % cx;
    const y0 = ((iy % cy) + cy) % cy;
    const x1 = (x0 + 1) % cx;
    const y1 = (y0 + 1) % cy;
    const a = get(g, y0 * cx + x0);
    const b = get(g, y0 * cx + x1);
    const c = get(g, y1 * cx + x0);
    const d = get(g, y1 * cx + x1);
    return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
  };
};
/** The same noise pre-sampled at every texel. */
const grid = (r: Random, cx: number, cy = cx): Float32Array => {
  const n = noise(r, cx, cy);
  const out = new Float32Array(SIZE * SIZE);
  for (let y = 0; y < SIZE; y += 1) for (let x = 0; x < SIZE; x += 1) out[y * SIZE + x] = n(x, y);
  return out;
};

/** A rounded clast with a lit upper rim, a darker lower rim and a one-texel cast shadow. */
const stamp = (tone: Float32Array, r: Random, cx: number, cy: number, rx: number, ry: number, body: number, hi: number, rim: number, shadow: number): void => {
  const inside = (dx: number, dy: number): boolean => (dx / rx) * (dx / rx) + (dy / ry) * (dy / ry) <= 1;
  const ex = Math.ceil(rx) + 1;
  const ey = Math.ceil(ry) + 1;
  for (let dy = -ey; dy <= ey; dy += 1) {
    for (let dx = -ex; dx <= ex; dx += 1) {
      if (!inside(dx, dy)) continue;
      const top = !inside(dx, dy + 1);
      const bottom = !inside(dx, dy - 1);
      const left = !inside(dx - 1, dy);
      tone[at(cx + dx, cy + dy)] = top && dx <= 0 ? hi : top || (left && dy >= 0) ? (hi + body) / 2 : bottom ? rim : body + (r() - 0.5) * L(0.5);
    }
  }
  for (let dx = -ex; dx <= ex; dx += 1) {
    for (let dy = -ey; dy <= ey; dy += 1) {
      if (!inside(dx, dy) || inside(dx, dy - 1)) continue;
      const i = at(cx + dx, cy + dy - 1);
      tone[i] = Math.min(get(tone, i), shadow);
    }
  }
};

const crack = (tone: Float32Array, r: Random, x0: number, y0: number, length: number, dark: number, lip: number | null, bias = 0.7): void => {
  let x = x0;
  let y = y0;
  let dx = r() < 0.5 ? -1 : 1;
  for (let n = 0; n < length; n += 1) {
    tone[at(x, y)] = dark;
    if (lip !== null) {
      const above = at(x, y + 1);
      if (get(tone, above) > dark + L(1)) tone[above] = Math.max(get(tone, above), lip);
    }
    if (r() < bias) x += dx;
    else y += r() < 0.5 ? -1 : 1;
    if (r() < 0.08) dx = -dx;
    if (r() < 0.06 && length - n > 5) crack(tone, r, x, y, Math.floor((length - n) * 0.5), dark, lip, bias);
  }
};

/** Ground: only texel-to-few-texel variation (the shader offsets each block and adds its own macro shift). */
const groundTones = (o: Options, r: Random): Float32Array => {
  const tone = new Float32Array(SIZE * SIZE);
  const a = grid(r, 32);
  const b = grid(r, 64);
  const c = grid(r, 128);
  for (let i = 0; i < SIZE * SIZE; i += 1) {
    const n = 0.5 * get(a, i) + 0.3 * get(b, i) + 0.2 * get(c, i);
    let t = L(3) + (n - 0.5) * L(1.5) + (r() - 0.5) * o.grain * L(2.1);
    const g = r();
    if (g < 0.03) t = L(3.8) + r() * L(0.6);
    else if (g < 0.06) t = L(2.2) + r() * L(0.5);
    tone[i] = t;
  }
  for (let n = 0; n < Math.round(o.pebble * k.pebbles); n += 1) {
    const x = Math.floor(r() * SIZE);
    const y = Math.floor(r() * SIZE);
    const size = r();
    if (size < 0.55) {
      if (size < 0.3) {
        tone[at(x, y)] = L(4 + r() * 0.6);
        tone[at(x, y - 1)] = L(2);
      }
      continue;
    }
    const rx = 0.9 + Math.pow(size - 0.55, 1.4) * 4.2;
    const ry = rx * (0.62 + 0.25 * r());
    const dark = r() < 0.28;
    stamp(tone, r, x, y, rx, ry, dark ? L(2.5) : L(4 + r() * 0.4), dark ? L(3.6) : L(5.3), dark ? L(1.9) : L(3), L(1.5));
  }
  for (let n = 0; n < Math.round(o.crack * k.groundCracks); n += 1) crack(tone, r, Math.floor(r() * SIZE), Math.floor(r() * SIZE), 6 + Math.floor(r() * 22), L(0.3), L(4.2));
  return tone;
};

type Lump = { x0: number; w: number; shift: number; joint: boolean; lean: number };
type Band = { y0: number; t: number; hard: boolean; base: number; wobble: Sampler; ripple: Sampler; jitter: Sampler; amp: number; lipCut: number; lumps: Lump[]; lumpAt: Int16Array };

/** Sedimentary cliff: irregular beds that swell, pinch and wander, lumps with rare leaning joints, lit lips and lenses. */
const rockTones = (o: Options, r: Random): Float32Array => {
  const tone = new Float32Array(SIZE * SIZE);
  const lam = grid(r, 24, 128);
  const hair = grid(r, 64, 256);
  const fine = grid(r, 64);
  const lipNoise = grid(r, 6, 16);
  const shade = grid(r, 8, 4);
  const heights: { y0: number; t: number }[] = [];
  for (let y = 0; y < SIZE; ) {
    const t = o.bandMin + Math.floor(Math.pow(r(), 1.6) * (o.bandMax - o.bandMin + 1)) + (r() < 0.18 ? o.bandMax : 0);
    heights.push({ y0: y, t });
    y += t;
  }
  const last = heights.at(-1);
  if (last !== undefined) last.t = SIZE - last.y0;
  if (last !== undefined && last.t < o.bandMin && heights.length > 1) {
    heights.pop();
    const prev = heights.at(-1);
    if (prev !== undefined) prev.t = SIZE - prev.y0;
  }
  const bands: Band[] = heights.map((h, n) => {
    const hard = (n % 2 === 0) !== r() < 0.35;
    const base = (hard ? L(3.2) : L(2.5)) + (r() - 0.5) * L(1.2);
    const wobble = noise(r, 2 + Math.floor(r() * 4), 1);
    const ripple = noise(r, 12, 1);
    const jitter = noise(r, 48, 1);
    const amp = (0.6 + r() * r() * 2.2) * Math.min(h.t, o.bandMax) * 0.45;
    const lipCut = r() < 0.5 ? -1 : 0.2 + r() * 0.25;
    const lumps: Lump[] = [];
    const min = hard ? 26 : 50;
    const span = hard ? 90 : 170;
    for (let x = 0; x < SIZE; ) {
      const w = Math.min(min + Math.floor(r() * span), SIZE - x);
      lumps.push({ x0: x, w, shift: (r() - 0.5) * L(0.9), joint: r() < (hard ? 0.3 : 0.08), lean: r() < 0.5 ? -1 : 1 });
      x += w;
    }
    const tail = lumps.at(-1);
    if (lumps.length > 1 && tail !== undefined && tail.w < min) {
      lumps.pop();
      const prev = lumps.at(-1);
      if (prev !== undefined) prev.w += tail.w;
    }
    const lumpAt = new Int16Array(SIZE);
    lumps.forEach((l, i) => {
      for (let x = l.x0; x < l.x0 + l.w; x += 1) lumpAt[x] = i;
    });
    return { ...h, hard, base, wobble, ripple, jitter, amp, lipCut, lumps, lumpAt };
  });
  const n = bands.length;
  const amp = 1 + o.wobble * 2.5;
  const boundary = (b: number, x: number): number => {
    const band = bands[b % n];
    if (band === undefined) return 0;
    const wander = b % n === 0 ? 0 : Math.round((band.wobble(x, 0) - 0.5) * 2 * band.amp + (band.ripple(x, 0) - 0.5) * amp + (band.jitter(x, 0) - 0.5) * 1.4);
    return band.y0 + (b >= n ? SIZE : 0) + wander;
  };
  for (let x = 0; x < SIZE; x += 1) {
    bands.forEach((band, b) => {
      const y0 = boundary(b, x);
      const t = Math.max(1, boundary(b + 1, x) - y0);
      const lump = band.lumps[band.lumpAt[x] ?? 0];
      if (lump === undefined) return;
      const edge = Math.min(x - lump.x0, lump.x0 + lump.w - 1 - x);
      const lip = get(lipNoise, at(x, band.y0)) > band.lipCut;
      for (let f = 0; f < t; f += 1) {
        const i = at(x, y0 + f);
        const u = t > 1 ? f / (t - 1) : 0.5;
        const ends = lump.joint ? (1 - Math.min(edge, 4) / 4) * L(0.9) : 0;
        let v = band.base + lump.shift + (u - 0.5) * L(1.6) - ends + (get(lam, i) - 0.5) * L(1.1) + (get(fine, i) - 0.5) * L(0.45) + (get(shade, i) - 0.5) * L(0.9) + (r() - 0.5) * o.grain * L(2);
        const h = get(hair, i);
        if (h < 0.12) v -= L(0.7);
        else if (h > 0.9) v += L(0.6);
        const top = f === t - 1 && t > 2;
        const jointX = lump.lean > 0 ? lump.x0 + (f >> 1) : lump.x0 - (f >> 1) + 1;
        if (lump.joint && Math.abs(x - jointX) < 1 && f < t - 1 && f >= (t - 1) * 0.45) v = Math.min(v, L(1.1));
        else if (top && lip) v = band.hard ? L(5.2) + r() * L(0.7) : L(4.1);
        else if (top) v = Math.max(v, L(3.1));
        else if (f === t - 2 && t > 4 && lip) v = Math.max(v, band.hard ? L(4) : L(3.3));
        else if (f === 0 && lip) v = band.hard ? L(0.3) : L(0.9);
        else if (f === 1 && t > 4 && lip) v = Math.min(v, band.hard ? L(1.4) : L(1.7));
        tone[i] = v;
      }
    });
  }
  for (let q = 0; q < Math.round(k.lenses[0] + o.crack * k.lenses[1]); q += 1) {
    const cx = Math.floor(r() * SIZE);
    const cy = Math.floor(r() * SIZE);
    const rx = 8 + r() * 26;
    const ry = 1.5 + r() * Math.max(1.5, o.bandMin * 0.7);
    const dv = (r() < 0.5 ? -1 : 1) * L(0.9 + r() * 0.6);
    for (let dx = -Math.ceil(rx); dx <= Math.ceil(rx); dx += 1) {
      const hh = ry * (1 - (dx / rx) * (dx / rx));
      if (hh < 0.5) continue;
      const top = Math.floor(hh);
      for (let dy = -top; dy <= top; dy += 1) {
        const i = at(cx + dx, cy + dy);
        const v = get(tone, i) + dv;
        tone[i] = dy === top ? Math.max(v, L(3.6)) : dy === -top ? Math.min(v, L(1.3)) : v;
      }
    }
  }
  for (let q = 0; q < Math.round(o.crack * k.pits); q += 1) {
    const x = Math.floor(r() * SIZE);
    const y = Math.floor(r() * SIZE);
    tone[at(x, y)] = Math.min(get(tone, at(x, y)), L(0.8));
    tone[at(x, y - 1)] = Math.max(get(tone, at(x, y - 1)), L(3.8));
  }
  return tone;
};

const timberTones = (o: Options, r: Random): Float32Array => {
  const tone = new Float32Array(SIZE * SIZE);
  const grain = grid(r, 128, 6);
  const warp = grid(r, 16, 8);
  const wear = grid(r, 16, 4);
  const boards: { x0: number; w: number; base: number }[] = [];
  for (let x = 0; x < SIZE; ) {
    const w = 5 + Math.floor(r() * 5);
    boards.push({ x0: x, w: Math.min(w, SIZE - x), base: L(3.1) + (r() - 0.5) * L(1.2) });
    x += w;
  }
  for (const board of boards) {
    for (let dx = 0; dx < board.w; dx += 1) {
      for (let y = 0; y < SIZE; y += 1) {
        const x = board.x0 + dx;
        const i = y * SIZE + x;
        const g = get(grain, at(Math.round(x + (get(warp, i) - 0.5) * 5), y));
        let v = board.base + (g - 0.5) * L(1.9) + (get(wear, i) - 0.5) * L(0.8) + (r() - 0.5) * o.grain * L(1.2);
        if (g < 0.2) v = L(1.2) + (r() - 0.5) * L(0.4);
        if (dx === 0) v = L(0.5);
        else if (dx === 1) v = Math.max(v, board.base + L(0.8));
        tone[at(x, y)] = v;
      }
    }
  }
  for (let q = 0; q < k.knots; q += 1) {
    const x = Math.floor(r() * SIZE);
    const y = Math.floor(r() * SIZE);
    const rx = 1.2 + r() * 1.3;
    const ry = rx * (1.3 + r() * 0.6);
    for (let dy = -4; dy <= 4; dy += 1) {
      for (let dx = -3; dx <= 3; dx += 1) {
        const d = (dx / rx) * (dx / rx) + (dy / ry) * (dy / ry);
        if (d <= 1) tone[at(x + dx, y + dy)] = d > 0.45 ? L(0.7) : L(2.2);
      }
    }
  }
  for (let q = 0; q < k.checks; q += 1) {
    const x = Math.floor(r() * SIZE);
    const y = Math.floor(r() * SIZE);
    const len = 3 + Math.floor(r() * 10);
    for (let j = 0; j < len; j += 1) tone[at(x, y + j)] = L(0.9);
  }
  return tone;
};

/** Rock caps: dark soil with crunchy grit and short hairline cracks; nothing larger than a few texels. */
const capTones = (o: Options, r: Random): Float32Array => {
  const tone = new Float32Array(SIZE * SIZE);
  const a = grid(r, 64);
  const b = grid(r, 128);
  for (let i = 0; i < SIZE * SIZE; i += 1) {
    let v = L(2.6) + (get(a, i) - 0.5) * L(2.1) + (get(b, i) - 0.5) * L(1.3) + (r() - 0.5) * o.grain * L(3);
    const g = r();
    if (g < 0.05) v += L(1.4);
    else if (g < 0.11) v -= L(1.4);
    tone[i] = v;
  }
  for (let q = 0; q < Math.round(o.crack * k.capCracks); q += 1) crack(tone, r, Math.floor(r() * SIZE), Math.floor(r() * SIZE), 3 + Math.floor(r() * 7), L(1.2), L(3.1));
  return tone;
};

/** Generator inputs in texel units, from the theme. */
const optionsOf = (m: Theme['tileMaterials']): Options => {
  const bandMin = Math.max(2, Math.round((m.strataBandMinPercent / 100) * m.texelDensity));
  return {
    grain: m.surfaceGrainPercent / 100,
    pebble: m.pebbleDensityPercent / 100,
    crack: m.crackDensityPercent / 100,
    wobble: Math.min(1, m.strataScalePercent / 100 / 2),
    bandMin,
    bandMax: Math.max(bandMin, Math.round((m.strataBandMaxPercent / 100) * m.texelDensity)),
  };
};

/** The atlas for a theme's generator settings (cached: one texture per distinct setting). */
export const pixelAtlas = (materials: Theme['tileMaterials']): THREE.DataTexture => {
  const options = optionsOf(materials);
  const key = JSON.stringify(options);
  const cached = cache.get(key);
  if (cached !== undefined) return cached;
  const channels = [groundTones(options, rng(k.seeds[0])), rockTones(options, rng(k.seeds[1])), timberTones(options, rng(k.seeds[2])), capTones(options, rng(k.seeds[3]))];
  const data = new Uint8Array(SIZE * SIZE * 4);
  channels.forEach((tone, c) => {
    for (let i = 0; i < tone.length; i += 1) data[i * 4 + c] = Math.round(Math.min(1, Math.max(0, get(tone, i))) * 255);
  });
  const texture = new THREE.DataTexture(data, SIZE, SIZE, THREE.RGBAFormat, THREE.UnsignedByteType);
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.generateMipmaps = false;
  texture.colorSpace = THREE.NoColorSpace;
  texture.name = 'pixel-atlas (shared, borrowed)';
  texture.needsUpdate = true;
  cache.set(key, texture);
  return texture;
};

/** Frees the atlases' GPU copies (their data stays; the next Medium frame uploads them again). */
export const freeAtlasGpu = (): void => {
  for (const texture of cache.values()) texture.dispose();
};

let holders = 0;
/** Holds the atlas cache for one view; the returned function lets go (once). The last holder to let go disposes it. */
export const holdAtlases = (): (() => void) => {
  holders += 1;
  let held = true;
  return () => {
    if (!held) return;
    held = false;
    holders -= 1;
    if (holders > 0) return;
    freeAtlasGpu();
    cache.clear();
  };
};
